import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { Box, Spinner, Center } from '@chakra-ui/react';
import { authState } from './state/authState';
import { authService, onAuthStateChange } from './services/api';
import Layout from './components/common/Layout';
import ProtectedRoute from './components/common/ProtectedRoute';
import ErrorBoundary, { RouteErrorBoundary } from './components/common/ErrorBoundary';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import CalculatorPage from './pages/CalculatorPage';
import AssessmentsPage from './pages/AssessmentsPage';
import PrintAssessmentPage from './pages/PrintAssessmentPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import AdminUsersPage from './pages/AdminUsersPage';
import AdminSettingsPage from './pages/AdminSettingsPage';

// Wrap component with route-level error boundary
const withErrorBoundary = (Component) => (
  <RouteErrorBoundary>
    <Component />
  </RouteErrorBoundary>
);

export const App = () => {
  const [auth, setAuth] = useRecoilState(authState);

  // Listen for auth state changes from API interceptor
  useEffect(() => {
    const unsubscribe = onAuthStateChange((type) => {
      if (type === 'logout') {
        setAuth({
          user: null,
          isAuthenticated: false,
          isLoading: false
        });
      }
    });

    return unsubscribe;
  }, [setAuth]);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await authService.getMe();
        setAuth({
          user: response.data.user,
          isAuthenticated: true,
          isLoading: false
        });
      } catch (err) {
        setAuth({
          user: null,
          isAuthenticated: false,
          isLoading: false
        });
      }
    };

    checkAuth();
  }, [setAuth]);

  // Auto-refresh token every 10 minutes while authenticated
  useEffect(() => {
    if (!auth.isAuthenticated) return;

    const interval = setInterval(async () => {
      try {
        await authService.refresh();
      } catch (err) {
        // Refresh failed - interceptor will handle logout
      }
    }, 10 * 60 * 1000); // 10 minutes

    return () => clearInterval(interval);
  }, [auth.isAuthenticated]);

  if (auth.isLoading) {
    return (
      <Center h="100vh">
        <Spinner size="xl" color="red.500" />
      </Center>
    );
  }

  return (
    <ErrorBoundary>
      <Box minH="100vh" bg="gray.50">
        <Routes>
          <Route path="/login" element={
            auth.isAuthenticated ? <Navigate to="/calculator" /> : <LoginPage />
          } />
          <Route path="/register" element={
            auth.isAuthenticated ? <Navigate to="/calculator" /> : <RegisterPage />
          } />

          {/* Print route - no layout wrapper */}
          <Route path="/print/:slug" element={
            <ProtectedRoute>{withErrorBoundary(PrintAssessmentPage)}</ProtectedRoute>
          } />

          <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
            <Route path="/calculator" element={withErrorBoundary(CalculatorPage)} />
            <Route path="/assessments" element={withErrorBoundary(AssessmentsPage)} />
                        <Route path="/admin" element={
              <ProtectedRoute requiredRoles={['super_admin', 'manager']}>
                {withErrorBoundary(AdminDashboardPage)}
              </ProtectedRoute>
            } />
            <Route path="/admin/users" element={
              <ProtectedRoute requiredRoles={['super_admin', 'manager']}>
                {withErrorBoundary(AdminUsersPage)}
              </ProtectedRoute>
            } />
            <Route path="/admin/settings" element={
              <ProtectedRoute requiredRoles={['super_admin', 'manager']}>
                {withErrorBoundary(AdminSettingsPage)}
              </ProtectedRoute>
            } />
          </Route>

          <Route path="/" element={<Navigate to={auth.isAuthenticated ? "/calculator" : "/login"} />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </Box>
    </ErrorBoundary>
  );
};

export default App;
